// src/components/quotes/QuoteStartModal.jsx
//
// "Start a quote" — the choice at CREATION: Blank · From a template · Copy an
// existing quote. Templates already worked, but only from a 140px dropdown
// inside a quote you had already created blank, so nobody found them before
// they needed them. This asks once, before the builder opens.
//
// ⚠ The creation path applies exactly ONE template, ONCE, by building the
// quote's structure from it (quoteTemplates.structureFromTemplate). It does
// NOT go through the builder's "Apply template…" dropdown, which is additive
// on purpose (it appends to a quote you are already building) and would be
// wrong here. Both stay; they do different jobs.
import React, { useMemo, useState } from "react";
import { formatQuoteNumber, matchesQuoteNumberSearch } from "./quoteRecord";
import { isSectionTemplate, templateItemCount } from "./quoteTemplates";

export default function QuoteStartModal({ backlogItem, templates = [], quotes = [], onBlank, onTemplate, onCopy, onCancel }) {
  const [search, setSearch] = useState("");
  const candidates = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = s
      ? quotes.filter((q) => matchesQuoteNumberSearch(q, search)
          || String(q.companyName || "").toLowerCase().includes(s)
          || String(q.title || "").toLowerCase().includes(s))
      : quotes;
    return [...list].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)).slice(0, 12);
  }, [quotes, search]);

  const subject = backlogItem?.subject && backlogItem.subject !== "New quote request" ? backlogItem.subject : null;

  /* ⚠ THE SHRINK CHAIN. Grid and flex items default to min-width:auto, whose
     floor is min-content, so a long template name forced the column, the panel,
     the grid and the modal itself wider than the viewport — the modal's
     maxWidth:100% cannot override min-width:auto. Ellipsising the span alone
     did nothing because every ancestor refused to shrink. minWidth:0 at every
     level, and minmax(0,1fr) columns, is the fix; a fixed px width would just
     move the failure to another viewport. Same bug as the client register
     (CLIENT_REGISTER_LAYOUT_BRIEF.md). */
  const panel = { background: "white", border: "1px solid var(--border)", borderRadius: 8, padding: 12, minWidth: 0 };
  const heading = { fontSize: 11, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 };
  const row = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--s2)", cursor: "pointer", fontSize: 11, minWidth: 0 };

  const quoteTitleAttr = (q) => [q.quoteNumber != null ? formatQuoteNumber(q.quoteNumber) : null, q.title || "(untitled quote)", q.companyName || null]
    .filter(Boolean).join(" — ");

  return (
    <div data-testid="quote-start-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(11,34,64,0.45)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onCancel}>
      {/* The two panels stack below ~620px — a media query, not a JS width
          listener. Inline styles cannot carry one, so the one class lives here. */}
      <style>{`.qsm-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px;min-width:0;}
@media (max-width:620px){.qsm-grid{grid-template-columns:minmax(0,1fr);}}`}</style>
      <div data-testid="quote-start-dialog"
        style={{ background: "var(--s1)", borderRadius: 10, padding: 22, width: "min(720px, 100%)", maxWidth: "100%", minWidth: 0, maxHeight: "92vh", overflowY: "auto", overflowX: "hidden", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 15, color: "var(--text)", marginBottom: 4 }}>
          Start a quote{subject ? ` — ${subject}` : ""}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 14, lineHeight: 1.6 }}>
          {backlogItem?.companyName ? <>For <strong>{backlogItem.companyName}</strong>. </> : null}
          Start blank, rebuild a saved template, or copy an existing quote. The builder opens on the new quote either way.
        </div>

        <div className="qsm-grid">
          <div style={{ ...panel, gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={heading}>Blank</div>
              <div style={{ fontSize: 11, color: "var(--text3)" }}>No sections yet — add them in the builder or ask the AI.</div>
            </div>
            <button className="btn btn-strat" data-testid="start-blank" style={{ fontSize: 11 }} onClick={onBlank}>Start blank ›</button>
          </div>

          <div style={panel} data-testid="start-templates">
            <div style={heading}>From a template</div>
            {templates.length === 0 ? (
              <div style={{ fontSize: 11, color: "var(--text3)", fontStyle: "italic" }}>No templates saved yet. Open a quote and use “Save as template”.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {templates.map((t) => (
                  <div key={t.id} role="button" tabIndex={0} data-testid={`start-template-${t.id}`} style={row}
                    onClick={() => onTemplate(t.id)} onKeyDown={(e) => { if (e.key === "Enter") onTemplate(t.id); }}>
                    <span title={t.name} style={{ fontWeight: 600, color: "var(--text)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</span>
                    <span style={{ color: "var(--text3)", flexShrink: 0 }}>
                      {isSectionTemplate(t) ? `${t.sections.length} section${t.sections.length === 1 ? "" : "s"} · ` : ""}{templateItemCount(t)} item{templateItemCount(t) === 1 ? "" : "s"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={panel} data-testid="start-copy">
            <div style={heading}>Copy an existing quote</div>
            <input className="inp" data-testid="start-copy-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Q number, client or title…" style={{ width: "100%", fontSize: 11, padding: "4px 8px", marginBottom: 6 }} />
            {candidates.length === 0 ? (
              <div style={{ fontSize: 11, color: "var(--text3)", fontStyle: "italic" }}>{quotes.length ? "No quotes match." : "No quotes yet."}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {candidates.map((q) => (
                  <div key={q.id} role="button" tabIndex={0} data-testid={`start-copy-${q.id}`} style={row}
                    onClick={() => onCopy(q.id)} onKeyDown={(e) => { if (e.key === "Enter") onCopy(q.id); }}>
                    <span title={quoteTitleAttr(q)} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {q.quoteNumber != null && <span style={{ fontWeight: 700, letterSpacing: "0.06em", color: "var(--text3)", marginRight: 6 }}>{formatQuoteNumber(q.quoteNumber)}</span>}
                      <span style={{ fontWeight: 600, color: "var(--text)" }}>{q.title || "(untitled quote)"}</span>
                      {q.companyName && <span style={{ color: "var(--strat)", marginLeft: 6 }}>{q.companyName}</span>}
                    </span>
                    <span style={{ color: "var(--text3)", flexShrink: 0, textTransform: "uppercase", fontSize: 9, fontWeight: 700 }}>{q.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button className="btn" style={{ fontSize: 11 }} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
